import React from "react";
import { Trash, Edit } from "lucide-react";
import { EDSButton } from "../../../../eds/EDSButton";
import { displayConfirmDialog } from "../../../../../utils/confirmDialog";
import { GroupedPreOrders } from "./PreOrderTypes";

type PreOrderListViewProps = {
  groupedPreOrderItems: GroupedPreOrders;
  hasActiveDisplayOptions: boolean;
  groupByTime: boolean;
  includeForNames: boolean;
  currency: string;
  handleEditPreOrderItemById: (preOrderId: string) => void;
  handleDeletePreOrderItem: (preOrderId: string) => void;
};

const PreOrderListView = ({
  groupedPreOrderItems,
  hasActiveDisplayOptions,
  groupByTime,
  includeForNames,
  currency,
  handleEditPreOrderItemById,
  handleDeletePreOrderItem
}: PreOrderListViewProps) => {
  return (
    <div className="space-y-4 mb-4">
      {Object.entries(groupedPreOrderItems).map(([groupKey, items]) => (
        <div key={groupKey} className="space-y-2">
          {hasActiveDisplayOptions && groupKey !== "all" && (
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {groupKey}
            </h4>
          )}

          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm table-fixed">
              <thead className="bg-muted">
                <tr>
                  {groupByTime && (
                    <th className="text-left p-2 font-medium w-16">Time</th>
                  )}
                  <th className="text-left p-2 font-medium">Item</th>
                  <th className="text-right p-2 font-medium w-12">Qty</th>
                  <th className="text-right p-2 font-medium w-20">Price</th>
                  <th className="text-right p-2 font-medium w-20">Total</th>
                  <th className="text-right p-2 font-medium w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) =>
                  item.isHeading ? (
                    <tr
                      key={`heading-${item.type}-${item.subType}`}
                      className="bg-muted/50"
                    >
                      <td
                        colSpan={groupByTime ? 6 : 5}
                        className="p-2 font-semibold text-xs uppercase tracking-wide"
                      >
                        {item.name}
                      </td>
                    </tr>
                  ) : (
                    <tr
                      key={`item-${item.name}-${item.time}-${item.price}-${item.preOrderIds.join("-")}`}
                      className="border-t border-border"
                    >
                      {groupByTime && (
                        <td className="p-2 text-muted-foreground">
                          {item.time || "No specified time"}
                        </td>
                      )}
                      <td className="p-2">
                        <div>{item.name}</div>
                        {includeForNames && item.customers.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            For:{" "}
                            {item.customers
                              .map((c) => {
                                const quantityStr =
                                  c.quantity > 1 ? ` x${c.quantity}` : "";
                                const seatStr = c.seat ? ` (${c.seat})` : "";
                                return `${c.name}${quantityStr}${seatStr}`;
                              })
                              .join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right">{item.totalQuantity}</td>
                      <td className="p-2 text-right">
                        {(item.price ?? 0).toLocaleString(undefined, {
                          style: "currency",
                          currency
                        })}
                      </td>
                      <td className="p-2 text-right">
                        {(item.totalCost ?? 0).toLocaleString(undefined, {
                          style: "currency",
                          currency
                        })}
                      </td>
                      <td className="p-2 text-right">
                        {item.preOrderIds.length === 1 && (
                          <div className="flex justify-end gap-1">
                            <EDSButton
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleEditPreOrderItemById(item.preOrderIds[0])
                              }
                              className="h-7 w-7 p-0"
                              aria-label={`Edit ${item.name}`}
                              title="Edit"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </EDSButton>
                            <EDSButton
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                const confirmDelete =
                                  await displayConfirmDialog({
                                    title: "Delete Pre-Order?",
                                    message: `Are you sure you wish to delete this pre-order from this booking?`,
                                    accept: "Confirm",
                                    cancel: "Cancel"
                                  });
                                if (confirmDelete) {
                                  handleDeletePreOrderItem(item.preOrderIds[0]);
                                }
                              }}
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              aria-label={`Delete ${item.name}`}
                              title="Delete"
                            >
                              <Trash className="h-3.5 w-3.5" />
                            </EDSButton>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PreOrderListView;
